import { isNotSet, isSet } from './validation'

export const nanCorrection = (value: number): number => (isNaN(value) ? null : value)

export const truncateToDecimals = (value: number, decimalPlaces = 3): number => {
  if (!Number.isFinite(value) || decimalPlaces < 0) return value

  const factor = 10 ** decimalPlaces
  return Math.trunc(value * factor) / factor
}

/**
 * Arredonda para um número de casas, sem formatar.
 *
 * É o par de `truncateToDecimals` para o lado da escrita: o valor que chega a um campo sem o piloto
 * ter digitado (troca de unidade, "Tanque cheio", pílula de peso padrão, importação de template)
 * entra no formulário com a precisão da grandeza, e não com a cauda que a conversão produziu.
 */
export const roundToDecimals = (value: number, decimalPlaces?: number): number => {
  if (decimalPlaces === undefined || decimalPlaces < 0) return value
  if (!Number.isFinite(value)) return value

  return Number(value.toFixed(decimalPlaces))
}

export const formatNumberTruncated = (num: number, decimalPlaces = 3) => {
  const truncated = truncateToDecimals(num, decimalPlaces)

  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimalPlaces
  }).format(truncated)
}

/**
 * O traço do Peso & Balanceamento: o que se imprime quando o número não existe — não é zero, é
 * ausência. Braço de tanque de braço variável sem combustível é o caso comum (`interpolateArm`
 * devolve `null` para peso zero).
 */
export const WB_EMPTY_VALUE = '--'

/**
 * Casas decimais com que cada grandeza do Peso & Balanceamento é impressa, em toda superfície —
 * campo, total, tabela e manifesto.
 *
 * PLA-334: é a tabela do cliente móvel (`WB_DECIMALS` em `pilotapp/src/utils/weightAndBalance.ts`),
 * adotada aqui para que o piloto que confira o manifesto do celular contra o do web leia os mesmos
 * números. Antes o peso saía com uma casa e o percentual da corda pegava carona no formatador de
 * braço, com três.
 */
export const WB_DECIMALS = {
  weight: 2,
  fuelQuantity: 2,
  arm: 3,
  moment: 2,
  percent: 2
} as const

/**
 * O estilo numérico de todo valor de Peso & Balanceamento: **arredondado** às casas da sua grandeza,
 * milhar agrupado, vírgula decimal, zeros à direita descartados — um inteiro sai sem vírgula.
 *
 * PLA-334: arredonda, nunca trunca. Truncar escondia até 0,09 de peso e imprimia 92,769 como 92,7 ao
 * lado de um campo que mostrava 92,77.
 *
 * PLA-318: o traço nasce aqui. Sem isso, `formatNumberTruncated(null)` imprimia `"0"` — um braço no
 * datum num manifesto assinado — e cada chamador precisaria repetir a guarda. Zero continua zero: é
 * valor legítimo, não ausência. `Number.isFinite` cobre também `±Infinity`, que um momento dividido
 * por um índice zero produz e que sairia como `"∞"`.
 *
 * O zero é normalizado **depois** do arredondamento porque é o arredondamento que cria o caso: um
 * `-0,004` de resíduo vira `-0`, e `Intl` imprime o sinal. As etapas de consumo de combustível são
 * negativas por construção, então o `-0` chega aqui pelos dois caminhos.
 */
const formatWBValue = (num: number, decimalPlaces: number) => {
  if (isNotSet(num) || !Number.isFinite(num)) return WB_EMPTY_VALUE

  const rounded = roundToDecimals(num, decimalPlaces)
  const normalized = rounded === 0 ? 0 : rounded

  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimalPlaces
  }).format(normalized)
}

/**
 * Se um valor passa de um máximo **como os dois são impressos**.
 *
 * PLA-336: o estado guarda precisão total e a tela arredonda (item 28), então comparar os números
 * crus recusa o que a tela mostra como igual. Um tanque de 348,25795 L cheio volta do armazenamento
 * como 348,26 e reprovava por 0,002 L — um aviso de "capacidade excedida em 0" numa ficha em que
 * nada mudou entre gravar e reabrir. Vale nos dois sentidos: o piloto que digita o "97,86" do
 * rótulo não está acima de uma capacidade de 97,8617.
 */
export const exceedsPrinted = (value: number, maximum: number, decimalPlaces: number): boolean =>
  roundToDecimals(value, decimalPlaces) > roundToDecimals(maximum, decimalPlaces)

export const formatWBWeight = (num: number) => formatWBValue(num, WB_DECIMALS.weight)
export const formatWBFuelQuantity = (num: number) => formatWBValue(num, WB_DECIMALS.fuelQuantity)
export const formatWBArm = (num: number) => formatWBValue(num, WB_DECIMALS.arm)
export const formatWBMoment = (num: number) => formatWBValue(num, WB_DECIMALS.moment)
export const formatWBPercent = (num: number) => formatWBValue(num, WB_DECIMALS.percent)

export const formatNumber = (num: number, decimalPlaces?: number) => {
  if (isNotSet(num)) return null
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: isSet(decimalPlaces) ? decimalPlaces : Number.isInteger(num) ? 0 : 1,
    maximumFractionDigits: isSet(decimalPlaces) ? decimalPlaces : 1
  })
    .format(num)
    .replace(/,0+$/, '')
}

/**
 * O `toFixed` no padrão pt-BR: sempre `decimalPlaces` casas (zeros à direita mantidos), milhar com
 * ponto e decimal com vírgula — `195.1` com uma casa sai `"195,1"`, `1234` com zero casas, `"1.234"`.
 *
 * PLA-384: é o par de `formatNumber` para os valores que já eram impressos com casas fixas (distância
 * da perna, combustível, preço) e que não devem perder o zero à direita ao trocar de separador.
 * Um `-0` vindo do arredondamento sai como `0`, sem sinal.
 */
export const formatNumberFixed = (num: number, decimalPlaces = 0) => {
  if (isNotSet(num) || !Number.isFinite(num)) return null

  const rounded = roundToDecimals(num, decimalPlaces)
  const normalized = rounded === 0 ? 0 : rounded

  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces
  }).format(normalized)
}

export const toNonNegativeOrZero = (value: number): number => {
  if (isNotSet(value) || !Number.isFinite(value)) return 0
  return Math.max(0, value)
}
